import React, { createContext, useContext, useState, useEffect } from 'react';
import { ordersApi } from '../api';

const InvoiceContext = createContext();

export const useInvoice = () => {
  const context = useContext(InvoiceContext);
  if (!context) {
    throw new Error('useInvoice must be used within an InvoiceProvider');
  }
  return context;
};

export const InvoiceProvider = ({ children }) => {
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastFetchDate, setLastFetchDate] = useState(null);

  // Generate invoice number format: INV-YYYYMMDD-001
  const generateInvoiceNumber = (sequenceNumber) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    const formattedNumber = sequenceNumber.toString().padStart(3, '0');
    return `INV-${year}${month}${day}-${formattedNumber}`;
  };

  // Extract sequence number from invoice number
  const extractSequenceNumber = (invoiceNumber) => {
    if (!invoiceNumber || typeof invoiceNumber !== 'string') return 0;
    const match = invoiceNumber.match(/INV-\d{8}-(\d{3})$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Get the latest invoice number from orders
  const fetchLatestInvoiceNumber = async () => {
    try {
      setLoading(true);
      
      // Get today's date for filtering
      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      
      // Fetch orders for today
      const orders = await ordersApi.list({
        from: todayStr,
        to: todayStr
      });
      
      // Filter orders that have invoice numbers (name field starting with INV-)
      const invoiceOrders = orders.filter(order => 
        order.name && order.name.startsWith('INV-') && order.name.match(/INV-\d{8}-\d{3}$/)
      );
      
      // Find the highest sequence number for today
      let maxSequence = 0;
      invoiceOrders.forEach(order => {
        const sequence = extractSequenceNumber(order.name);
        if (sequence > maxSequence) {
          maxSequence = sequence;
        }
      });
      
      // Generate next invoice number
      const nextSequence = maxSequence + 1;
      const nextInvoiceNumber = generateInvoiceNumber(nextSequence);
      
      setCurrentInvoiceNumber(nextInvoiceNumber);
      setLastFetchDate(today);
      
      console.log('Fetched latest invoice number:', {
        ordersCount: orders.length,
        invoiceOrdersCount: invoiceOrders.length,
        maxSequence,
        nextSequence,
        nextInvoiceNumber
      });
      
      return nextInvoiceNumber;
    } catch (error) {
      console.error('Error fetching latest invoice number:', error);
      // Fallback to generating a new number
      const fallbackNumber = generateInvoiceNumber(1);
      setCurrentInvoiceNumber(fallbackNumber);
      return fallbackNumber;
    } finally {
      setLoading(false);
    }
  };

  // Generate next invoice number (increment current)
  const generateNextInvoiceNumber = () => {
    const currentSequence = extractSequenceNumber(currentInvoiceNumber);
    const nextSequence = currentSequence + 1;
    const nextNumber = generateInvoiceNumber(nextSequence);
    setCurrentInvoiceNumber(nextNumber);
    return nextNumber;
  };

  // Refresh invoice number from server
  const refreshInvoiceNumber = async () => {
    return await fetchLatestInvoiceNumber();
  };

  // Initialize on mount
  useEffect(() => {
    fetchLatestInvoiceNumber();
  }, []);

  // Check if we need to refresh (new day)
  useEffect(() => {
    const checkForNewDay = () => {
      const today = new Date();
      const todayStr = today.toDateString();
      const lastFetchStr = lastFetchDate ? lastFetchDate.toDateString() : null;
      
      if (lastFetchStr && lastFetchStr !== todayStr) {
        console.log('New day detected, refreshing invoice number');
        fetchLatestInvoiceNumber();
      }
    };

    // Check every minute
    const interval = setInterval(checkForNewDay, 60000);
    
    // Also check on focus (user came back to tab)
    const handleFocus = () => checkForNewDay();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [lastFetchDate]);

  const value = {
    currentInvoiceNumber,
    loading,
    generateNextInvoiceNumber,
    refreshInvoiceNumber,
    fetchLatestInvoiceNumber
  };

  return (
    <InvoiceContext.Provider value={value}>
      {children}
    </InvoiceContext.Provider>
  );
};
